import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ArrowLeft, Ban, Building2, Calendar, CheckCircle2, Mail, MapPin, Phone, Trash2, UserRound, XCircle } from "lucide-react";

const ROLE_LABEL = {
  private: "Privat",
  dealer: "Händler",
};

const STATUS_LABEL = {
  approved: "Freigegeben",
  pending: "Neu",
  rejected: "Abgelehnt",
  suspended: "Gesperrt",
};

const STATUS_BADGE = {
  approved: "bg-green-50 text-green-700 border-green-200",
  pending: "bg-yellow-50 text-yellow-800 border-yellow-200",
  rejected: "bg-red-50 text-red-700 border-red-200",
  suspended: "bg-orange-50 text-orange-700 border-orange-200",
};

export default function AdminRegistrations() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [loading, setLoading] = useState(true);

  const load = () => {
    setLoading(true);
    api.get("/admin/registrations")
      .then(r => {
        const next = r.data.items || [];
        setItems(next);
        setSelectedId(current => current || next[0]?.id || null);
      })
      .catch(e => toast.error(formatApiError(e.response?.data?.detail)))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    if (!selectedId) {
      setDetail(null);
      return;
    }
    api.get(`/admin/registrations/${selectedId}`)
      .then(r => setDetail(r.data))
      .catch(e => {
        setDetail(null);
        toast.error(formatApiError(e.response?.data?.detail));
      });
  }, [selectedId]);

  const filtered = useMemo(() => items.filter(item => {
    if (roleFilter && item.role !== roleFilter) return false;
    if (statusFilter && item.status !== statusFilter) return false;
    return true;
  }), [items, roleFilter, statusFilter]);

  useEffect(() => {
    if (filtered.length === 0) {
      setSelectedId(null);
      return;
    }
    if (!filtered.some(item => item.id === selectedId)) {
      setSelectedId(filtered[0].id);
    }
  }, [filtered, selectedId]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/registrations/${id}/status`, { status });
      toast.success("Status aktualisiert");
      await load();
      setSelectedId(id);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const deleteUser = async (user) => {
    const label = user.role === "dealer" ? (user.company || user.name || user.email) : (user.name || user.email);
    if (!window.confirm(`${ROLE_LABEL[user.role] || "Nutzer"} "${label}" wirklich dauerhaft löschen? Inserate, Anfragen, Bewertungen und hochgeladene Dateien werden entfernt.`)) return;
    try {
      await api.delete(`/admin/registrations/${user.id}`);
      toast.success("Nutzer gelöscht");
      setDetail(null);
      setSelectedId(null);
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const selected = detail?.user || items.find(item => item.id === selectedId);

  return (
    <div className="page-wrap py-10">
      <button onClick={() => nav("/admin")} data-testid="back-to-admin" className="inline-flex items-center gap-2 text-xs text-gray-500 hover:text-[#16A34A] mb-6">
        <ArrowLeft className="w-3.5 h-3.5" /> Admin-Übersicht
      </button>

      <div className="flex flex-wrap items-end justify-between gap-5">
        <div>
          <div className="swiss-label text-[#16A34A]">Admin</div>
          <h1 className="font-display text-3xl font-bold tracking-tight mt-2">Neue Anmeldungen</h1>
        </div>
        <div className="flex flex-wrap gap-2">
          <FilterButton active={roleFilter === ""} onClick={() => setRoleFilter("")}>Alle Rollen</FilterButton>
          <FilterButton active={roleFilter === "private"} onClick={() => setRoleFilter("private")}>Privat</FilterButton>
          <FilterButton active={roleFilter === "dealer"} onClick={() => setRoleFilter("dealer")}>Händler</FilterButton>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-8">
        <Stat label="Anmeldungen" value={items.length} />
        <Stat label="Privat" value={items.filter(i => i.role === "private").length} />
        <Stat label="Händler" value={items.filter(i => i.role === "dealer").length} />
        <Stat label="Neu" value={items.filter(i => i.status === "pending").length} accent />
      </div>

      <div className="flex flex-wrap gap-2 mt-8">
        {["", "pending", "approved", "rejected", "suspended"].map(status => (
          <FilterButton key={status} active={statusFilter === status} onClick={() => setStatusFilter(status)}>
            {status === "" ? "Alle Status" : STATUS_LABEL[status]}
          </FilterButton>
        ))}
      </div>

      <div className="grid lg:grid-cols-[minmax(0,1.08fr)_minmax(360px,0.92fr)] gap-6 mt-5">
        <div className="swiss-card overflow-x-auto">
          <table className="w-full text-sm" data-testid="admin-registrations-table">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left swiss-label p-4">Anmeldung</th>
                <th className="text-left swiss-label p-4">Rolle</th>
                <th className="text-left swiss-label p-4">Status</th>
                <th className="text-left swiss-label p-4">Datum</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(item => (
                <tr
                  key={item.id}
                  data-testid={`registration-row-${item.id}`}
                  onClick={() => setSelectedId(item.id)}
                  className={`border-b border-gray-100 hover:bg-gray-50 cursor-pointer ${selectedId === item.id ? "bg-green-50/50" : ""}`}
                >
                  <td className="p-4">
                    <div className="font-semibold">{item.role === "dealer" ? item.company || item.name : item.name}</div>
                    <div className="text-xs text-gray-500">{item.email}</div>
                  </td>
                  <td className="p-4"><RoleBadge role={item.role} /></td>
                  <td className="p-4"><StatusBadge status={item.status} /></td>
                  <td className="p-4 text-gray-600">{formatDate(item.created_at)}</td>
                </tr>
              ))}
              {!loading && filtered.length === 0 && (
                <tr><td colSpan={4} className="p-8 text-center text-gray-500">Keine Anmeldungen gefunden.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={4} className="p-8 text-center text-gray-500">Lade...</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="swiss-card p-6 min-h-[520px]" data-testid="registration-preview">
          {!selected ? (
            <div className="h-full flex items-center justify-center text-sm text-gray-500">Keine Anmeldung ausgewählt.</div>
          ) : (
            <RegistrationPreview data={detail} user={selected} onStatus={setStatus} onDelete={deleteUser} />
          )}
        </div>
      </div>
    </div>
  );
}

function RegistrationPreview({ data, user, onStatus, onDelete }) {
  const stats = data?.stats || {};
  const vehicles = data?.vehicles || [];

  return (
    <div>
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <RoleBadge role={user.role} />
            <StatusBadge status={user.status} />
          </div>
          <h2 className="font-display text-2xl font-bold tracking-tight mt-3">{user.role === "dealer" ? user.company || user.name : user.name}</h2>
          {user.role === "dealer" && <div className="text-sm text-gray-500 mt-1">{user.name}</div>}
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mt-5">
        {user.status !== "approved" && (
          <ActionButton onClick={() => onStatus(user.id, "approved")} color="green" icon={CheckCircle2}>Freigeben</ActionButton>
        )}
        {user.status !== "rejected" && user.status !== "approved" && (
          <ActionButton onClick={() => onStatus(user.id, "rejected")} color="red" icon={XCircle}>Ablehnen</ActionButton>
        )}
        {user.status === "approved" && (
          <ActionButton onClick={() => onStatus(user.id, "suspended")} color="orange" icon={Ban}>Sperren</ActionButton>
        )}
        <ActionButton onClick={() => onDelete(user)} color="gray" icon={Trash2}>Löschen</ActionButton>
      </div>

      <div className="grid grid-cols-3 gap-3 mt-6">
        <MiniStat label="Inserate" value={stats.vehicles_total ?? "-"} />
        <MiniStat label="Aktiv" value={stats.vehicles_published ?? "-"} />
        <MiniStat label="Anfragen" value={stats.inquiries_total ?? "-"} />
      </div>

      <div className="mt-7 space-y-5">
        <Section title="Kontakt">
          <Info icon={Mail} label="E-Mail" value={user.email} />
          <Info icon={Phone} label="Telefon" value={user.phone || "-"} />
          <Info icon={Calendar} label="Registriert" value={formatDateTime(user.created_at)} />
        </Section>

        <Section title="Person">
          <Info icon={UserRound} label="Anrede" value={user.salutation || "-"} />
          <Info icon={UserRound} label="Vorname" value={user.first_name || "-"} />
          <Info icon={UserRound} label="Nachname" value={user.last_name || "-"} />
        </Section>

        <Section title="Adresse">
          <Info icon={MapPin} label="Straße / Nr." value={`${user.street || "-"} ${user.house_number || ""}`.trim()} />
          <Info icon={MapPin} label="PLZ / Ort" value={`${user.zip_code || "-"} ${user.city || ""}`.trim()} />
          <Info icon={MapPin} label="Land" value={user.country || "-"} />
        </Section>

        {user.role === "dealer" && (
          <Section title="Händlerdaten">
            <Info icon={Building2} label="Firma" value={user.company || "-"} />
            <Info icon={Building2} label="Steuernummer" value={user.tax_number || "-"} />
          </Section>
        )}
      </div>

      {vehicles.length > 0 && (
        <div className="mt-7">
          <div className="swiss-label text-gray-500 mb-3">Inserate</div>
          <div className="space-y-2">
            {vehicles.slice(0, 5).map(v => (
              <Link key={v.id} to={`/admin/fahrzeug/${v.id}`} className="block border border-gray-200 p-3 hover:border-[#16A34A]">
                <div className="font-semibold text-sm">{v.title}</div>
                <div className="text-xs text-gray-500 mt-1">{v.brand} {v.model} · {v.year} · {v.status}</div>
              </Link>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FilterButton({ active, onClick, children }) {
  return (
    <button onClick={onClick} className={`text-xs px-3 py-1.5 border font-semibold ${active ? "bg-[#0A0A0A] text-white border-[#0A0A0A]" : "border-gray-300 text-gray-700 hover:border-[#16A34A]"}`}>
      {children}
    </button>
  );
}

function RoleBadge({ role }) {
  const color = role === "dealer" ? "bg-blue-50 text-blue-700 border-blue-200" : "bg-gray-50 text-gray-700 border-gray-200";
  return <span className={`text-xs font-bold px-2 py-1 border ${color}`}>{ROLE_LABEL[role] || role}</span>;
}

function StatusBadge({ status }) {
  return <span className={`text-xs font-bold px-2 py-1 border ${STATUS_BADGE[status] || "bg-gray-50 text-gray-700 border-gray-200"}`}>{STATUS_LABEL[status] || status}</span>;
}

function Stat({ label, value, accent }) {
  return (
    <div className={`swiss-card p-5 ${accent && value > 0 ? "border-[#16A34A] border-2" : ""}`}>
      <div className="swiss-label text-gray-500">{label}</div>
      <div className={`font-display text-3xl font-extrabold mt-2 ${accent && value > 0 ? "text-[#16A34A]" : ""}`}>{value}</div>
    </div>
  );
}

function MiniStat({ label, value }) {
  return (
    <div className="border border-gray-200 p-3">
      <div className="swiss-label text-gray-500">{label}</div>
      <div className="font-display text-xl font-bold mt-1">{value}</div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <div>
      <div className="swiss-label text-gray-500 mb-3">{title}</div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

function Info({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-3 text-sm">
      <Icon className="w-4 h-4 text-[#16A34A] mt-0.5 flex-shrink-0" />
      <div className="min-w-0">
        <div className="text-xs text-gray-500">{label}</div>
        <div className="font-semibold text-gray-900 break-words">{value}</div>
      </div>
    </div>
  );
}

function ActionButton({ icon: Icon, color, onClick, children }) {
  const colors = {
    green: "bg-green-600 hover:bg-green-700",
    red: "bg-red-600 hover:bg-red-700",
    orange: "bg-orange-600 hover:bg-orange-700",
    gray: "bg-gray-900 hover:bg-red-700",
  };
  return (
    <button onClick={onClick} className={`inline-flex items-center gap-2 text-white text-sm font-semibold px-4 py-2 ${colors[color]}`}>
      <Icon className="w-4 h-4" /> {children}
    </button>
  );
}

function formatDate(value) {
  return value ? new Date(value).toLocaleDateString("de-DE") : "-";
}

function formatDateTime(value) {
  return value ? new Date(value).toLocaleString("de-DE") : "-";
}
